import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINRCompact } from "@/lib/format";
import {
    Rocket, Users, Sparkles, Globe, TrendingUp, Radio, Link2, Copy,
    QrCode, Plus, IndianRupee, Building2, Activity,
} from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, tone = "neutral", testId }) {
    const cls = { gold: "ring-amber-200 bg-amber-50/50", emerald: "ring-emerald-100 bg-emerald-50/40", neutral: "ring-stone-200 bg-white" }[tone];
    return (
        <div className={`rounded-2xl ring-1 ${cls} p-5`} data-testid={testId}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={13} className="text-stone-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">{label}</span>
            </div>
            <div className="text-3xl font-semibold text-stone-900 tabular-nums leading-none">{value}</div>
            {sub && <div className="text-[11.5px] text-stone-500 mt-2">{sub}</div>}
        </div>
    );
}

function Section({ title, right, children }) {
    return (
        <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
                <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{title}</h2>
                {right}
            </div>
            {children}
        </div>
    );
}

function CreateCampaignForm({ onCreated }) {
    const [f, setF] = useState({ source: "whatsapp", medium: "community", campaign: "global_launch" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/growth/campaign-links", f);
            onCreated();
        } finally { setBusy(false); }
    };
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="cc-new-form">
            <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Source</label>
                <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
                        data-testid="cc-source"
                        className="block mt-1 px-3 py-1.5 rounded-md border border-stone-200 text-sm">
                    {["whatsapp","whatsapp_status","whatsapp_customer_group","linkedin","instagram","facebook","email","google","referral","direct"].map((s) =>
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                    )}
                </select>
            </div>
            <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Medium</label>
                <input value={f.medium} onChange={(e) => setF({ ...f, medium: e.target.value })}
                       data-testid="cc-medium"
                       className="block mt-1 px-3 py-1.5 rounded-md border border-stone-200 text-sm" />
            </div>
            <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500">Campaign</label>
                <input value={f.campaign} onChange={(e) => setF({ ...f, campaign: e.target.value })}
                       data-testid="cc-campaign"
                       className="block mt-1 px-3 py-1.5 rounded-md border border-stone-200 text-sm" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="cc-create-btn"
                    className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white px-4 py-2 rounded-md text-sm">
                <Plus size={13} /> Create link
            </button>
        </div>
    );
}

export default function SuperAdminLaunchCentre() {
    const [data, setData] = useState(null);
    const [links, setLinks] = useState([]);
    const [feed, setFeed] = useState([]);

    const load = () => {
        api.get("/growth/launch-centre").then((r) => setData(r.data)).catch(() => setData(null));
        api.get("/growth/campaign-links").then((r) => setLinks(r.data.rows || [])).catch(() => {});
        api.get("/growth/activity-feed").then((r) => setFeed(r.data.rows || [])).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    if (!data) return <div className="p-6 text-stone-500">Loading Launch Centre…</div>;
    const lt = data.launch_total;
    const conv = data.conversion;

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="launch-centre-page">
            <div className="mb-6">
                <div className="eyebrow text-amber-700">Growth Intelligence</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Launch Centre
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Privacy-first acquisition · campaign performance · launch scorecard. No financial values enter analytics.
                </p>
            </div>

            {/* Today */}
            <Section title="Today">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                    <StatCard icon={Users} label="Visitors" value={data.today.visitors} testId="today-visitors" />
                    <StatCard icon={Sparkles} label="Demo Opens" value={data.today.demo} testId="today-demo" />
                    <StatCard icon={Rocket} label="New Signups" value={data.today.signups} testId="today-signups" />
                </div>
            </Section>

            {/* Launch total */}
            <Section title="Launch total">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <StatCard icon={Users} label="Visitors" value={lt.visitors} tone="gold" testId="lt-visitors" />
                    <StatCard icon={Globe} label="Countries" value={lt.countries_reached} testId="lt-countries" />
                    <StatCard icon={Rocket} label="Signups" value={lt.signup_complete} sub={`${lt.signup_started} started`} testId="lt-signups" />
                    <StatCard icon={Sparkles} label="Trials" value={lt.trials} testId="lt-trials" />
                    <StatCard icon={IndianRupee} label="Paying" value={lt.paying} tone="emerald" testId="lt-paying" />
                    <StatCard icon={Activity} label="Demo Sessions" value={lt.demo} testId="lt-demo" />
                    <StatCard icon={TrendingUp} label="Visitor → Signup" value={`${conv.visitor_to_signup}%`} testId="lt-conv-vs" />
                    <StatCard icon={TrendingUp} label="Trial → Paid" value={`${conv.trial_to_paid}%`} testId="lt-conv-tp" />
                </div>
            </Section>

            {/* Top sources + countries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="lc-sources">
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Top sources · first-touch</div>
                    {data.top_sources.length === 0 ? <p className="text-sm text-stone-500">No attribution captured yet — tracking begins from your first campaign link.</p> :
                        data.top_sources.map((s) => (
                            <div key={s.source} className="flex items-center gap-3 py-1">
                                <span className="text-[13px] font-medium text-stone-800 w-40 capitalize">{s.source.replace(/_/g, " ")}</span>
                                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (s.visits / Math.max(1, lt.visitors)) * 100)}%` }} />
                                </div>
                                <span className="text-[12px] tabular-nums text-stone-600 w-10 text-right">{s.visits}</span>
                            </div>
                        ))}
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="lc-countries">
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Country performance</div>
                    {data.countries.length === 0 ? <p className="text-sm text-stone-500">Countries appear once visitors arrive with attribution.</p> :
                        data.countries.map((c) => (
                            <div key={c.country} className="flex items-center gap-3 py-1">
                                <span className="text-[13px] font-medium text-stone-800 w-16">{c.country}</span>
                                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, (c.visitors / Math.max(1, lt.visitors)) * 100)}%` }} />
                                </div>
                                <span className="text-[12px] tabular-nums text-stone-600 w-10 text-right">{c.visitors}</span>
                            </div>
                        ))}
                </div>
            </div>

            {/* Campaign links */}
            <Section title="Campaign links · trackable UTM URLs">
                <CreateCampaignForm onCreated={load} />
                <div className="mt-3 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="cc-list">
                    {links.length === 0 ? (
                        <div className="p-6 text-sm text-stone-500 text-center">No campaigns yet — create your first trackable link above.</div>
                    ) : links.map((l) => (
                        <div key={l.code} className="px-5 py-3 border-b border-stone-100 last:border-b-0 flex items-center gap-4 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-semibold text-stone-900">{l.campaign} · <span className="text-stone-500 capitalize font-normal">{l.source.replace(/_/g, " ")}</span></div>
                                <div className="text-[11px] text-stone-500 mt-0.5 truncate">{l.url}</div>
                            </div>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700">
                                <Users size={11} /> {l.visits} visits
                            </span>
                            <button onClick={() => navigator.clipboard.writeText(l.url)}
                                    data-testid={`cc-copy-${l.code}`}
                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-stone-200 hover:bg-stone-50">
                                <Copy size={11} /> Copy
                            </button>
                            <a href={l.qr_url} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-stone-200 hover:bg-stone-50">
                                <QrCode size={11} /> QR
                            </a>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Live feed */}
            <Section title="Live launch feed" right={<span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><Radio size={11} /> Anonymised</span>}>
                <div className="rounded-2xl border border-stone-200 bg-white p-4 max-h-96 overflow-auto" data-testid="lc-feed">
                    {feed.length === 0 ? <p className="text-sm text-stone-500">Activity will appear here as visitors arrive.</p> :
                        feed.map((e, i) => (
                            <div key={i} className="py-1.5 flex items-center gap-3 text-[12.5px] border-b border-stone-100 last:border-b-0">
                                <span className="text-stone-400 tabular-nums w-14 text-[10.5px]">{new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                <span className="uppercase tracking-widest text-[10px] font-bold text-amber-700 w-32 truncate">{(e.kind || "event").replace(/_/g, " ")}</span>
                                <span className="text-stone-600 flex-1 min-w-0">from {e.country || "??"}{e.plan ? ` · ${e.plan}` : ""}</span>
                            </div>
                        ))}
                </div>
            </Section>
        </div>
    );
}
